import {
  type AnnouncementItem,
  type Audience,
  useAnnouncementActions,
  useAnnouncements,
  useAuth,
  useClasses,
  useCurrentContext,
} from '@shkola/api';
import { BellIcon } from '@phosphor-icons/react/Bell';
import type { TFunction } from 'i18next';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Field, Modal, Select, Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  PageHead,
  PillIcon,
  TaskArt,
  grad,
  pillClass,
} from '../design/forms';
import { CheckIcon, PlusIcon } from '../design/icons';
import { dayMonthShort } from '../student/home/model';

/*
 * «Оголошення» in «Форми 2» (28.10): the title on the paper with how many are new; the new ones first as
 * soft cards with the megaphone big and quiet behind (to confirm — pink, to read — sky) and the one
 * button to mark them; beside them, on a white block, everything else as a ruled list — the day as a
 * small calendar leaf, the title, the words, who wrote it; the author sees how many read it and can
 * remind the rest.
 */

/** My member ids in this school: receipts are per member. */
export function useMyMemberIds(schoolId: string): string[] {
  const { me } = useAuth();
  return (me?.memberships ?? []).filter((m) => m.school_id === schoolId).map((m) => m.member_id);
}

const MONTH_SHORT = new Intl.DateTimeFormat('uk-UA', { timeZone: 'UTC', month: 'short' });

/** A label: a soft rectangle in a gradient, never a pill. */
function Tag({ tone, white, children }: { tone: Grad; white?: boolean; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={
        white
          ? { backgroundColor: 'rgb(255 255 255 / 0.75)', color: GRAD[tone].ink }
          : { backgroundImage: grad(tone), color: GRAD[tone].ink }
      }
    >
      {children}
    </span>
  );
}

function NewAnnouncement({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const classes = useClasses(ctx.schoolId);
  const actions = useAnnouncementActions();
  const homeroom = ctx.kind === 'teacher' ? ctx.homeroomClass : undefined;
  const audiences: Audience[] =
    ctx.kind === 'admin'
      ? ['school', 'staff', 'class', 'class_parents']
      : ['class', 'class_parents'];
  const [audience, setAudience] = useState<Audience>(audiences[0] as Audience);
  const [classId, setClassId] = useState(homeroom?.id ?? '');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [requiresAck, setRequiresAck] = useState(false);
  const [error, setError] = useState<string>();
  const needsClass = audience === 'class' || audience === 'class_parents';
  const klass = homeroom?.id ?? (classId || classes.data?.[0]?.id);

  return (
    <Modal title={t('announcements.new')} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(undefined);
          actions.publish
            .mutateAsync({
              schoolId: ctx.schoolId,
              audience,
              classId: needsClass ? (klass ?? null) : null,
              title,
              body,
              requiresAck,
            })
            .then(onClose, () => setError(t('common.error')));
        }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Select
            label={t('announcements.audience')}
            value={audience}
            onChange={(e) => setAudience(e.target.value as Audience)}
          >
            {audiences.map((a) => (
              <option key={a} value={a}>
                {t(`announcements.audiences.${a}`)}
              </option>
            ))}
          </Select>
          {needsClass &&
            (homeroom ? (
              <Badge tone="brand">{homeroom.label}</Badge>
            ) : (
              <Select
                aria-label={t('announcements.audience')}
                value={klass ?? ''}
                onChange={(e) => setClassId(e.target.value)}
              >
                {(classes.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </Select>
            ))}
        </div>
        <Field
          id="ann-title"
          label={t('announcements.titleLabel')}
          required
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <label className="block">
          <span className="mb-1.5 block text-[13px] font-medium" style={{ color: INK_SOFT }}>
            {t('announcements.bodyLabel')}
          </span>
          <textarea
            required
            maxLength={5000}
            className="min-h-32 w-full rounded-[14px] bg-white p-3.5 text-[15px] leading-6 outline-none ring-1 ring-[#E4E0D7] ring-inset focus:ring-2 focus:ring-[var(--sh-dark)]"
            style={{ color: INK }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-2.5 text-[14px]" style={{ color: INK }}>
          <input
            type="checkbox"
            className="size-4 accent-[var(--sh-dark)]"
            checked={requiresAck}
            onChange={(e) => setRequiresAck(e.target.checked)}
          />
          {t('announcements.requiresAck')}
        </label>
        {error && (
          <p role="alert" className="text-[14px]" style={{ color: GRAD.coral.ink }}>
            {error}
          </p>
        )}
        <Button type="submit" disabled={!title.trim() || !body.trim() || actions.publish.isPending}>
          {actions.publish.isPending ? t('announcements.publishing') : t('announcements.publish')}
        </Button>
      </form>
    </Modal>
  );
}

/** Who it is for: «Усій школі», «Класу та батькам · 7-Б». */
function audienceOf(t: TFunction, item: AnnouncementItem) {
  return `${t(`announcements.audiences.${item.audience}`)}${item.classLabel ? ` · ${item.classLabel}` : ''}`;
}

/** A new one: a soft card with the megaphone behind, its words and the one button. */
function FreshCard({ item, memberIds }: { item: AnnouncementItem; memberIds: string[] }) {
  const { t } = useTranslation();
  const actions = useAnnouncementActions();
  const tone: Grad = item.requiresAck ? 'pink' : 'sky';
  return (
    <article
      className="relative isolate flex min-w-0 flex-col overflow-hidden rounded-[28px] p-5 md:p-6"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      <TaskArt
        kind="ack"
        tone={tone}
        size={150}
        ghost
        className="pointer-events-none absolute -top-4 -right-5 -z-10"
      />
      <div className="flex flex-wrap gap-1.5 pr-16">
        <Tag tone={tone} white>
          {dayMonthShort(item.publishedAt.slice(0, 10))}
        </Tag>
        <Tag tone={tone} white>
          {audienceOf(t, item)}
        </Tag>
      </div>
      <h2
        className="mt-3 font-display text-[24px] leading-[30px] font-light tracking-[-0.025em]"
        style={{ color: INK }}
      >
        {item.title}
      </h2>
      <p className="mt-2 text-[15px] leading-6 whitespace-pre-line" style={{ color: INK }}>
        {item.body}
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        {item.author && (
          <span className="text-[13px]">{t('announcements.from', { author: item.author })}</span>
        )}
        <button
          type="button"
          className={`${pillClass('light')} ml-auto disabled:opacity-50`}
          disabled={actions.markRead.isPending}
          onClick={() => actions.markRead.mutate({ announcementId: item.id, memberIds })}
        >
          {item.requiresAck ? t('announcements.ack') : t('announcements.markRead')}
          <PillIcon icon={CheckIcon} />
        </button>
      </div>
    </article>
  );
}

/** One of the list: the day as a leaf, the title and the words, who wrote it; for the author — who read. */
function Row({ item }: { item: AnnouncementItem }) {
  const { t } = useTranslation();
  const actions = useAnnouncementActions();
  const [reminded, setReminded] = useState<string>();
  const notRead = item.stats ? item.stats.recipients - item.stats.read : 0;
  const day = item.publishedAt.slice(0, 10);
  return (
    <li className="flex gap-4 border-t border-[#E4E0D7] py-4">
      <span className="flex w-12 shrink-0 flex-col items-center pt-0.5 md:w-14">
        <span
          className="font-display text-[28px] leading-8 font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {Number(day.slice(8, 10))}
        </span>
        <span className="text-[12px]" style={{ color: INK_SOFT }}>
          {MONTH_SHORT.format(new Date(`${day}T12:00:00Z`))}
        </span>
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Tag tone="stone">{audienceOf(t, item)}</Tag>
          {item.isRecipient && item.readAt && item.requiresAck && (
            <Tag tone="mint">
              <CheckIcon size={12} weight="bold" aria-hidden />
              {t('announcements.acknowledged')}
            </Tag>
          )}
          {item.stats && (
            <Tag tone={notRead > 0 ? 'lavender' : 'mint'}>
              {t('announcements.read', { read: item.stats.read, total: item.stats.recipients })}
            </Tag>
          )}
        </div>
        <h3 className="mt-2 text-[17px] leading-6 font-medium" style={{ color: INK }}>
          {item.title}
        </h3>
        <p
          className="mt-1 max-w-[780px] text-[15px] leading-6 whitespace-pre-line"
          style={{ color: INK_SOFT }}
        >
          {item.body}
        </p>
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
          {item.author && (
            <span className="text-[13px]" style={{ color: INK_FAINT }}>
              {t('announcements.from', { author: item.author })}
            </span>
          )}
          {notRead > 0 &&
            (reminded ? (
              <Tag tone="mint">{reminded}</Tag>
            ) : (
              <button
                type="button"
                className={`${pillClass('light', 'sm')} ml-auto disabled:opacity-50`}
                disabled={actions.remind.isPending}
                onClick={() =>
                  actions.remind.mutate(item.id, {
                    onSuccess: (n) => setReminded(t('announcements.reminded', { count: n })),
                    onError: () => setReminded(t('announcements.remindedRecently')),
                  })
                }
              >
                {t('announcements.remind', { count: notRead })}
                <PillIcon icon={BellIcon} size="sm" />
              </button>
            ))}
        </div>
      </div>
    </li>
  );
}

export function AnnouncementsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const memberIds = useMyMemberIds(ctx.schoolId);
  const list = useAnnouncements(ctx.schoolId, memberIds);
  const [creating, setCreating] = useState(false);
  const canPublish =
    ctx.kind === 'admin' || (ctx.kind === 'teacher' && ctx.homeroomClass !== undefined);
  const all = list.data ?? [];
  const fresh = all.filter((a) => a.isRecipient && !a.readAt);
  const rest = all.filter((a) => !(a.isRecipient && !a.readAt));
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('announcements.title')}
        note={fresh.length > 0 ? t('notifications.unread', { count: fresh.length }) : undefined}
      >
        {canPublish && (
          <button
            type="button"
            className={`${pillClass('dark')} max-lg:w-11 max-lg:justify-center max-lg:px-0`}
            onClick={() => setCreating(true)}
          >
            <span className="max-lg:sr-only">{t('announcements.new')}</span>
            <PillIcon icon={PlusIcon} />
          </button>
        )}
      </PageHead>
      {list.isPending ? (
        <Block>
          <Spinner label={t('common.loading')} />
        </Block>
      ) : all.length === 0 ? (
        <Block className="items-center py-14 text-center">
          <TaskArt kind="ack" tone="sky" size={96} />
          <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
            {t('announcements.empty')}
          </p>
        </Block>
      ) : (
        <div
          className={`grid grid-cols-1 items-start gap-4 ${fresh.length > 0 && rest.length > 0 ? 'xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]' : ''}`}
        >
          {fresh.length > 0 && (
            <div
              className={`grid min-w-0 grid-cols-1 items-start gap-3 ${rest.length === 0 ? 'lg:grid-cols-2' : 'xl:sticky xl:top-20'}`}
            >
              {fresh.map((a) => (
                <FreshCard key={a.id} item={a} memberIds={memberIds} />
              ))}
            </div>
          )}
          {rest.length > 0 && (
            <Block>
              {fresh.length > 0 && <Head title={t('announcements.earlier')} />}
              <ul
                className={`flex flex-col ${fresh.length > 0 ? '' : '[&>li:first-child]:border-t-0 [&>li:first-child]:pt-0'}`}
              >
                {rest.map((a) => (
                  <Row key={a.id} item={a} />
                ))}
              </ul>
            </Block>
          )}
        </div>
      )}
      {creating && <NewAnnouncement onClose={() => setCreating(false)} />}
    </div>
  );
}
